import React, { FC, ReactElement, useContext, useState } from 'react'
import Drawer from '@material-ui/core/Drawer'
import { useHotkeys } from 'react-hotkeys-hook'
import { useBranding } from '../../../../React/Common/Hooks/useBranding'
import { Branding } from '../../../../React/Common/API/BrandingAPI'
import { FullscreenBackground } from '../../Molecules/FullscreenBackground/FullscreenBackground'
import { LanguageUtils } from '../../../../React/Common/Utils/LanguageUtils'
import { BgType, ReactState } from '../../../../React/Common/Types/Types'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../../../React/Common/Contexts'
import { Authentication as LanguageAuthentication } from '../../../../../i18n'
import Loader from '../../../../React/Common/Components/Loader'
import { DarkModeType, useDarkMode } from '../../../../React/Common/Hooks/useDarkMode'
import { ServiceMessages } from '../../Molecules/ServiceMessages/ServiceMessages'
import { ServiceMessagesTimeline } from '../../Molecules/ServiceMessages/ServiceMessagesTimeline'
import IconButton, { IconButtonType } from '../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../React/Common/Components/Icon'
import { URLConstructor } from '../../../../NonReact/utils/URLBuilder'
import { APIElements } from '../../../../NonReact/constants/ApiConstants'
import { RouteID } from '../../Molecules/Header/Types/Types'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'

type Props = {
    children: ReactElement
    showSubtitle?: boolean
}

export const WelcomeContainer: FC<Props> = ({ children, showSubtitle = true }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(LanguageAuthentication.NAME)

    const branding: Nullable<Branding> = useBranding()

    const [isLoaded, setIsLoaded]: ReactState<boolean> = useState<boolean>(false)
    const [isBrowserProfileOptionShown, setIsBrowserProfileOptionShown]: ReactState<boolean> = useState<boolean>(false)

    const { darkModeSwitch: darkMode }: DarkModeType = useDarkMode()

    useHotkeys('ctrl+alt+c', (): void =>
        setIsBrowserProfileOptionShown((browserProfileOptionShown: boolean): boolean => !browserProfileOptionShown)
    )

    return (
        <>
            {!isLoaded ? (
                <div className='router-loader-container'>
                    <Loader />
                </div>
            ) : (
                <></>
            )}
            <FullscreenBackground
                type={(branding?.BrandingConfig?.imagery?.authBgType?.toUpperCase?.() as BgType) ?? BgType.Image}
                src={branding?.BrandingConfig?.imagery?.authBg ?? EMPTY_STRING}
                alt='Main background'
                setIsLoaded={setIsLoaded}
            >
                <div className='welcome-container'>
                    <div className='wc-left-container wc-side-open'>
                        <div className='wc-container'>
                            <ServiceMessages />

                            <div className='wc-main-logo-container'>
                                <a
                                    href={branding?.BrandingConfig?.links?.mainSiteLink?.url ?? EMPTY_STRING}
                                    target={branding?.BrandingConfig?.links?.mainSiteLink?.target}
                                    title={branding?.BrandingConfig?.links?.mainSiteLink?.title}
                                    className='wc-main-logo'
                                >
                                    <img
                                        className='wc-logo'
                                        src={branding?.BrandingConfig?.imagery?.mainLogo}
                                        alt='Main logo'
                                    />
                                </a>
                            </div>

                            {showSubtitle ? (
                                <div className='wc-subtitle-container'>
                                    {branding?.BrandingConfig?.languages?.authSubtitle ? (
                                        <h1 className='wc-subtitle'>
                                            {LanguageUtils.formatLanguageEntry(
                                                branding.BrandingConfig?.languages?.authSubtitle ?? EMPTY_STRING
                                            )}
                                        </h1>
                                    ) : (
                                        <></>
                                    )}
                                    {branding?.BrandingConfig?.languages?.authSubtitle2 ? (
                                        <h2 className='wc-subtitle'>
                                            {LanguageUtils.formatLanguageEntry(
                                                branding.BrandingConfig?.languages?.authSubtitle2 ?? EMPTY_STRING
                                            )}
                                        </h2>
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    </div>
                    <Drawer className='wc-drawer wc-drawer-open' variant='persistent' anchor='right' open={true}>
                        {children}
                        <div className='wc-drawer-options'>
                            <ServiceMessagesTimeline />
                            {darkMode}
                            {isBrowserProfileOptionShown ? (
                                <IconButton
                                    iconName={IconName.WRENCH}
                                    type={IconButtonType.LINK}
                                    href={URLConstructor.newInstance({
                                        pathParams: [APIElements.CCS, APIElements.APP_V2, RouteID.CHANGE_USER_AGENT]
                                    }).constructURL()}
                                    title={language.get(LanguageAuthentication.Authentication_ChangeBrowserProfile)}
                                />
                            ) : (
                                <></>
                            )}
                        </div>
                    </Drawer>
                </div>
            </FullscreenBackground>
        </>
    )
}
